import { z } from 'zod';

export type CardPaymentField = 'cardName' | 'cardNumber' | 'cardExpiry' | 'cardCvv';
export type CardPaymentFieldErrors = Partial<Record<CardPaymentField, string>>;

export interface CardPaymentFormValues {
  cardName: string;
  cardNumber: string;
  cardExpiry: string;
  cardCvv: string;
  cvvLength?: number;
}

export interface CardPaymentValidationResult {
  isValid: boolean;
  errors: CardPaymentFieldErrors;
}

export const detectCardNetwork = (cardNumber: string): string => {
  if (/^3[47]/.test(cardNumber)) return 'amex';
  if (/^4/.test(cardNumber)) return 'visa';
  if (/^(5[1-5]|2(2[2-9]|[3-6]|7[01]|720))/.test(cardNumber)) return 'mastercard';
  if (/^(50|5[6-9]|6)/.test(cardNumber)) return 'maestro';
  if (/^(60|65|81|82|508|353|356)/.test(cardNumber)) return 'rupay';
  if (/^35/.test(cardNumber)) return 'jcb';
  return '';
};

export const isLuhnValid = (cardNumber: string): boolean => {
  if (!/^\d+$/.test(cardNumber)) return false;

  let sum = 0;
  let shouldDouble = false;

  for (let i = cardNumber.length - 1; i >= 0; i -= 1) {
    let digit = Number(cardNumber[i]);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

export const getExpiryParts = (expiry: string): [string, string] => {
  const digits = expiry.replace(/\D/g, '').slice(0, 4);
  return [digits.slice(0, 2), digits.slice(2, 4)];
};

export const formatCardExpiry = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)} / ${digits.slice(2, 4)}`;
};

const cardPaymentFormSchema = z
  .object({
    cardName: z
      .string()
      .trim()
      .min(1, 'Name on card is required.')
      .regex(/^[A-Za-z ]+$/, 'Name on card can contain only letters.'),
    cardNumber: z.string(),
    cardExpiry: z.string(),
    cardCvv: z.string(),
    cvvLength: z.number().int().min(3).max(4).optional(),
  })
  .superRefine((value, ctx) => {
    const cardNumber = value.cardNumber.replace(/\D/g, '');
    if (!cardNumber || cardNumber.length < 13 || cardNumber.length > 19 || !isLuhnValid(cardNumber)) {
      ctx.addIssue({
        code: 'custom',
        path: ['cardNumber'],
        message: 'Enter a valid card number',
      });
    }

    const [monthValue, yearValue] = getExpiryParts(value.cardExpiry);
    const month = parseInt(monthValue, 10);
    const year = parseInt(yearValue, 10) + 2000;
    const today = new Date();
    const isPastExpiry =
      year < today.getFullYear() ||
      (year === today.getFullYear() && month < today.getMonth() + 1);

    if (
      monthValue.length !== 2 ||
      yearValue.length !== 2 ||
      Number.isNaN(month) ||
      Number.isNaN(year) ||
      month < 1 ||
      month > 12 ||
      isPastExpiry
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['cardExpiry'],
        message: 'Enter a valid expiry date',
      });
    }

    const expectedCvvLength = value.cvvLength === 4 ? 4 : 3;
    const cardCvv = value.cardCvv.replace(/\D/g, '');
    if (cardCvv.length !== expectedCvvLength) {
      ctx.addIssue({
        code: 'custom',
        path: ['cardCvv'],
        message: `Enter a valid ${expectedCvvLength}-digit CVV`,
      });
    }
  });

function flattenCardPaymentErrors(error: z.ZodError): CardPaymentFieldErrors {
  const formatted: CardPaymentFieldErrors = {};
  const fieldErrors = error.flatten().fieldErrors as Partial<
    Record<CardPaymentField, string[] | undefined>
  >;

  for (const [field, messages] of Object.entries(fieldErrors) as Array<
    [CardPaymentField, string[] | undefined]
  >) {
    if (messages?.[0]) {
      formatted[field] = messages[0];
    }
  }

  return formatted;
}

export function validateCardPaymentFormValues(
  values: CardPaymentFormValues
): CardPaymentValidationResult {
  const result = cardPaymentFormSchema.safeParse({
    ...values,
    cvvLength: values.cvvLength ?? 3,
  });

  return result.success
    ? { isValid: true, errors: {} }
    : { isValid: false, errors: flattenCardPaymentErrors(result.error) };
}
